import { wrapIndex } from '../core/math';
import { SURFACES } from '../physics/constants';
import { mulberry32 } from './rng';
import type { Track } from './generate';

export type WeatherId = 'dry' | 'damp' | 'wet';

export interface Weather {
  id: WeatherId;
  name: string;
  blurb: string;
  /** Uniform multiplier on track grip. */
  gripScale: number;
  /**
   * Extra grip loss per centerline sample, 0..1, from standing water. Patches
   * sit in specific places, so a wet lap is not merely "everything is slower" —
   * it moves individual braking points and rewards knowing where the water is.
   */
  patches: Float32Array;
  /** Effective mu at each centerline index, already including patches. */
  muAt: Float64Array;
}

const PRESETS: Record<WeatherId, { name: string; blurb: string; gripScale: number; patchiness: number }> = {
  dry: { name: 'Dry', blurb: 'Full grip.', gripScale: 1, patchiness: 0 },
  damp: {
    name: 'Damp',
    blurb: 'Greasy in places. Watch for the dark patches.',
    gripScale: 0.86,
    patchiness: 0.5,
  },
  wet: {
    name: 'Wet',
    blurb: 'Standing water. Brake far earlier than feels sensible.',
    gripScale: 0.68,
    patchiness: 1,
  },
};

/** Rolls the day's weather. Mostly dry, so wet days feel like an event. */
export function rollWeather(seed: number): WeatherId {
  const rng = mulberry32(seed ^ 0x5eed1234);
  const r = rng.next();
  if (r < 0.68) return 'dry';
  if (r < 0.9) return 'damp';
  return 'wet';
}

export function buildWeather(track: Track, seed: number, id: WeatherId): Weather {
  const preset = PRESETS[id];
  const n = track.center.length;
  const patches = new Float32Array(n);

  if (preset.patchiness > 0) {
    const rng = mulberry32(seed ^ 0xa17b2c);
    const count = 2 + Math.floor(rng.next() * 4 * preset.patchiness);
    for (let p = 0; p < count; p++) {
      const centre = Math.floor(rng.next() * n);
      // 12–36 m of affected track, tapering at the edges so grip never steps.
      const halfWidth = Math.round((6 + rng.next() * 12) / track.spacing);
      const depth = (0.25 + rng.next() * 0.45) * preset.patchiness;
      for (let d = -halfWidth; d <= halfWidth; d++) {
        const t = 1 - Math.abs(d) / halfWidth;
        const idx = wrapIndex(centre + d, n);
        patches[idx] = Math.max(patches[idx], depth * t * t);
      }
    }
  }

  const base = SURFACES.track.mu * preset.gripScale;
  const muAt = new Float64Array(n);
  for (let i = 0; i < n; i++) muAt[i] = base * (1 - patches[i]);

  return {
    id,
    name: preset.name,
    blurb: preset.blurb,
    gripScale: preset.gripScale,
    patches,
    muAt,
  };
}

export function dryWeather(track: Track): Weather {
  return buildWeather(track, 0, 'dry');
}
